import { useCallback, useEffect, useState } from "react";
import { checkUpstoxStatus, initiateUpstoxTokenGeneration, refreshUpstoxToken } from "../services/upstoxAuth";

const getDefaultMessage = ({ authenticated, hasCredentials }) => {
  if (authenticated) return "Upstox token is live and available.";
  if (!hasCredentials) return "No Upstox token was found in the database.";
  return "Saved Upstox token is expired or invalid. Generate a new token to continue.";
};

export default function UpstoxTokenStatus({ onAuthenticated }) {
  const [status, setStatus] = useState({
    loading: true,
    authenticated: false,
    hasCredentials: false,
    dbAvailable: true,
    message: "Checking Upstox token status...",
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  const updateStatus = useCallback(
    (result) => {
      setStatus({
        loading: false,
        authenticated: result.authenticated,
        hasCredentials: result.hasCredentials,
        dbAvailable: result.dbAvailable !== undefined ? result.dbAvailable : true,
        message: result.message || getDefaultMessage(result),
      });
      if (typeof onAuthenticated === "function") {
        onAuthenticated(result.authenticated);
      }
    },
    [onAuthenticated]
  );

  const loadStatus = useCallback(async () => {
    setError(null);
    setStatus((prev) => ({ ...prev, loading: true }));
    try {
      const result = await checkUpstoxStatus();
      updateStatus(result);

      // If database server is waking up, retry checking in 5 seconds
      if (!result.authenticated && result.dbAvailable === false) {
        setTimeout(() => {
          loadStatus();
        }, 5000);
      }

      return result;
    } catch (err) {
      const message = err?.message || "Unable to determine token status.";
      setStatus({
        loading: false,
        authenticated: false,
        hasCredentials: false,
        dbAvailable: false,
        message,
      });
      setError(message);
      if (typeof onAuthenticated === "function") {
        onAuthenticated(false);
      }
      return { authenticated: false, hasCredentials: false, dbAvailable: false, message };
    }
  }, [updateStatus, onAuthenticated]);

  const refreshStatus = useCallback(async () => {
    setError(null);
    setStatus((prev) => ({ ...prev, loading: true }));
    try {
      const result = await refreshUpstoxToken();
      updateStatus(result);

      // If database server is waking up, retry checking in 5 seconds
      if (!result.authenticated && result.dbAvailable === false) {
        setTimeout(() => {
          refreshStatus();
        }, 5000);
      }

      return result;
    } catch (err) {
      const message = err?.message || "Unable to refresh token status.";
      setStatus({
        loading: false,
        authenticated: false,
        hasCredentials: false,
        dbAvailable: false,
        message,
      });
      setError(message);
      if (typeof onAuthenticated === "function") {
        onAuthenticated(false);
      }
      return { authenticated: false, hasCredentials: false, dbAvailable: false, message };
    }
  }, [updateStatus, onAuthenticated]);

  const generateToken = useCallback(async () => {
    setError(null);
    setIsAuthenticating(true);
    try {
      await initiateUpstoxTokenGeneration();
      const result = await refreshStatus();
      if (result.authenticated && typeof onAuthenticated === "function") {
        onAuthenticated(true);
      }
    } catch (err) {
      setError(err?.message || "Failed to generate token.");
    } finally {
      setIsAuthenticating(false);
    }
  }, [refreshStatus, onAuthenticated]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const showGenerateButton = !status.loading && !status.authenticated && status.hasCredentials && status.dbAvailable !== false;

  return (
    <div className="mb-6 rounded-3xl border border-slate-700 bg-slate-950/80 p-4 shadow-xl">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.32em] text-sky-300">Upstox Token</p>
          <p className="text-sm leading-6 text-slate-200">{status.message}</p>
        </div>

        {showGenerateButton && (
          <button
            type="button"
            onClick={generateToken}
            disabled={isAuthenticating}
            className="inline-flex items-center justify-center rounded-full bg-sky-500 px-5 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:bg-slate-700"
          >
            {isAuthenticating ? "⏳ Authorizing..." : "Generate New Token"}
          </button>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
      {!status.loading && !status.authenticated && !status.hasCredentials && (
        <p className="mt-3 text-sm text-amber-300">
          No token record was found in the database. Please authenticate from the Options tab or consult the backend.
        </p>
      )}
    </div>
  );
}
